import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  Text,
  useToast,
  HStack,
  VStack,
  Box,
} from '@chakra-ui/react';
import { Copy } from 'lucide-react';
import { useCopyList } from '@/api';
import { useNavigate } from '@tanstack/react-router';

interface CopyListModalProps {
  isOpen: boolean;
  onClose: () => void;
  listName: string;
  urlKey: string;
}

export function CopyListModal({
  isOpen,
  onClose,
  listName,
  urlKey
}: CopyListModalProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const [copyListName, setCopyListName] = useState(`Copy of ${listName}`);
  const { mutateAsync: copyList, isPending: isCopying } = useCopyList();

  // Reset the input when the modal opens
  useEffect(() => {
    if (isOpen) {
      setCopyListName(`Copy of ${listName}`);
    }
  }, [isOpen, listName]);

  const handleCopy = async () => {
    if (!copyListName.trim()) {
      return;
    }

    try {
      const result = await copyList({
        urlKey,
        newListName: copyListName
      });

      toast({
        title: "List copied successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();

      // Navigate to the new copied list
      if (result?.list?.urlKey) {
        navigate({ to: '/lists/$urlKey', params: { urlKey: result.list.urlKey } });
      }
    } catch (error) {
      console.error('Failed to copy list:', error);
      toast({
        title: "Failed to copy list",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCopy();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="blue.100" borderRadius="var(--r-inner)" p={2}>
              <Copy size={20} color="var(--chakra-colors-blue-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                Copy List
              </Text>
              <Text fontSize="xs" color="var(--card-text-3)" fontWeight="normal">
                Create your own version
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text mb={3} color="var(--card-text-2)">Create a copy of "{listName}" with a new name:</Text>
          <Input
            value={copyListName}
            onChange={(e) => setCopyListName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="New list name"
            autoFocus
          />
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="var(--card-divider)" pt={4}>
          <HStack spacing={3} w="100%">
            <Button variant="ghost" flex={1} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="blue.500"
              color="white"
              _hover={{ bg: 'blue.600' }}
              onClick={handleCopy}
              isLoading={isCopying}
              isDisabled={!copyListName.trim()}
              leftIcon={<Copy size={16} />}
              flex={1}
            >
              Copy
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}